/* CHUNWU FOUR — subtle GPU wet-sidewalk visual. Self contained WebGL1 shader,
   conservative DPR and frame throttling, no external CDN, no permissions.
   DOM and page links remain functional if all GPU work fails. */
(() => {
  'use strict';
  const root = document.querySelector('[data-chunwu-four]');
  if (!root || root.dataset.rainReady) return;
  root.dataset.rainReady = 'true';
  const canvas = root.querySelector('[data-chunwu-rain]');
  if (!canvas || !window.WebGLRenderingContext ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      window.matchMedia('print').matches) return;
  let gl;
  try { gl = canvas.getContext('webgl', { alpha: true, antialias: false, depth:false, stencil:false, powerPreference:'low-power' }); }
  catch (_) { return; }
  if (!gl) return;
  const vertex = `attribute vec2 position;
    varying vec2 vUv;
    void main(){vUv=position*.5+.5;gl_Position=vec4(position,0.,1.);}`;
  const fragment = `precision mediump float;
    varying vec2 vUv;
    uniform vec2 uRes; uniform float uTime; uniform vec2 uPointer;
    float rnd(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}
    void main(){
      vec2 uv = vUv;
      vec2 pos=(uv-.5);pos.x*=uRes.x/uRes.y;
      float horizon=.44 + uPointer.y*.025;
      float horizonSoft=smoothstep(horizon-.06,horizon+.075,uv.y);
      vec3 sky=mix(vec3(.036,.066,.095),vec3(.15,.135,.12),clamp(uv.y,0.,1.));
      float x=pos.x+uPointer.x*.07;
      float block=floor((x+2.0)*15.0);
      float h=.1 + rnd(vec2(block,2.0))*.17;
      float building=step(horizon,uv.y)*step(uv.y,horizon+h)*(1.-step(.76,fract((x+2.0)*15.0)));
      sky=mix(sky,vec3(.018,.028,.04),building*.84);
      float windows=step(.90,fract((x+2.)*60.))*step(.79,fract((uv.y-horizon)*73.))*building;
      sky+=windows*vec3(.2,.13,.065);
      float pavement=1.-smoothstep(horizon-.035,horizon+.02,uv.y);
      float dist=1./max(.04,horizon-uv.y);
      float seams=(1.-smoothstep(.02,.10,abs(sin(dist*1.23))))*.1;
      float xe=(x+uPointer.x*.02)*dist*.18;
      seams+=(1.-smoothstep(.02,.06,abs(sin(xe*3.3))))*.065;
      float gloss=.2+.16*sin(xe*2.4+uTime*.05)*sin(dist*.95);
      float glow=pow(max(0.,1.-abs(x+.36)/(.12+dist*.007)),2.)*.24;
      float spark=pow(max(0.,1.-abs(x-.25)/(.07+dist*.004)),2.)*.22;
      vec3 asphalt=vec3(.046,.057,.064)+vec3(.20,.17,.12)*(seams+gloss*.37)+vec3(.43,.26,.12)*glow+vec3(.13,.22,.31)*spark;
      vec2 puddle=vec2((x+.14)*2.0,(uv.y-.18)*2.2);
      float pr=length(puddle);
      float rip=sin(pr*77.-uTime*3.5)*exp(-pr*11.)*.026;
      asphalt+=vec3(.6,.52,.43)*max(0.,rip);
      vec3 color=mix(sky,asphalt,pavement);
      vec2 rainUV=vec2(uv.x*45.,uv.y*21.);
      vec2 cell=floor(rainUV),f=fract(rainUV);
      float seed=rnd(vec2(cell.x,cell.y*.37));
      float streak=1.-smoothstep(.0,.055,abs(f.x-seed));
      streak*=1.-smoothstep(.11,.93,fract(f.y+uTime*(1.5+seed*2.)));
      color+=vec3(.32,.40,.45)*streak*.18;
      float vignette=1.-.62*smoothstep(.34,1.15,length(pos));
      gl_FragColor=vec4(color*vignette,.89);
    }`;
  function shader(type, source) {
    const s=gl.createShader(type);gl.shaderSource(s,source);gl.compileShader(s);
    if (!gl.getShaderParameter(s,gl.COMPILE_STATUS)) { const why=gl.getShaderInfoLog(s);gl.deleteShader(s);throw new Error(why); }
    return s;
  }
  let prog,vertexBuffer;
  try {
    prog=gl.createProgram();const vs=shader(gl.VERTEX_SHADER,vertex), fs=shader(gl.FRAGMENT_SHADER,fragment);
    gl.attachShader(prog,vs);gl.attachShader(prog,fs);gl.linkProgram(prog);
    gl.deleteShader(vs);gl.deleteShader(fs);
    if(!gl.getProgramParameter(prog,gl.LINK_STATUS))throw new Error(gl.getProgramInfoLog(prog));
    gl.useProgram(prog);vertexBuffer=gl.createBuffer();gl.bindBuffer(gl.ARRAY_BUFFER,vertexBuffer);
    gl.bufferData(gl.ARRAY_BUFFER,new Float32Array([-1,-1,1,-1,-1,1,1,1]),gl.STATIC_DRAW);
    const pos=gl.getAttribLocation(prog,'position');gl.enableVertexAttribArray(pos);gl.vertexAttribPointer(pos,2,gl.FLOAT,false,0,0);
  } catch(_) { canvas.style.display='none';if(prog)gl.deleteProgram(prog);return; }
  const uniforms={r:gl.getUniformLocation(prog,'uRes'),t:gl.getUniformLocation(prog,'uTime'),p:gl.getUniformLocation(prog,'uPointer')};
  const pointer=[0,0];let active=false,raf=0,last=0,elapsed=0,started=0,width=0,height=0,disposed=false;
  function resize(){const rect=canvas.getBoundingClientRect(),dpr=Math.min(window.devicePixelRatio||1,1.5);
    width=Math.min(1600,Math.floor(rect.width*dpr));height=Math.min(1300,Math.floor(rect.height*dpr));
    if(width<4||height<4)return;
    if(canvas.width!==width||canvas.height!==height){canvas.width=width;canvas.height=height;gl.viewport(0,0,width,height);}
  }
  function render(t){raf=0;if(!active||disposed)return;
    const interval=window.matchMedia('(max-width: 700px)').matches?33:20;
    if(t-last>=interval){if(!last)started=t;elapsed=Math.min(120,(t-started)/1000);last=t;resize();
      if(width>0&&height>0){gl.uniform2f(uniforms.r,width,height);gl.uniform1f(uniforms.t,elapsed);gl.uniform2f(uniforms.p,pointer[0],pointer[1]);gl.drawArrays(gl.TRIANGLE_STRIP,0,4);}}
    raf=requestAnimationFrame(render);
  }
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries=>{active=entries[0].isIntersecting && !document.hidden;if(active&&!raf)raf=requestAnimationFrame(render);if(!active&&raf){cancelAnimationFrame(raf);raf=0;}},{rootMargin:'80px'}) : null;
  if(observer)observer.observe(root); else {active=true;raf=requestAnimationFrame(render)}
  const visibility=()=>{active=(!observer || root.getBoundingClientRect().bottom>0)&&!document.hidden;if(active&&!raf)raf=requestAnimationFrame(render);if(!active&&raf){cancelAnimationFrame(raf);raf=0}};
  document.addEventListener('visibilitychange',visibility);
  root.addEventListener('pointermove',ev=>{if(ev.pointerType==='touch')return;const r=root.getBoundingClientRect();pointer[0]=(ev.clientX-r.left)/r.width-.5;pointer[1]=(ev.clientY-r.top)/r.height-.5;},{passive:true});
  canvas.addEventListener('webglcontextlost',ev=>{ev.preventDefault();active=false;disposed=true;if(raf)cancelAnimationFrame(raf);canvas.style.display='none';},{once:true});
  // The scene never blocks links, touch scrolling, or the purchase path.
})();
